import type { BobState } from "../types";
import { seedFailureCards } from "./engine/failures";
import {
  seedAgentCards,
  seedDemoDirectives,
  syncAgentsWithDirectives,
} from "./engine/pipeline";
import { buildSovereignStatus } from "./engine/exportCenter";

const STORAGE_KEY = "bob-sovereign-builder-v1";

function migrateState(raw: Partial<BobState>): BobState {
  const directives =
    raw.directives && raw.directives.length > 0 ? raw.directives : seedDemoDirectives();
  const agents =
    raw.agents && raw.agents.length > 0
      ? syncAgentsWithDirectives(raw.agents, directives)
      : syncAgentsWithDirectives(seedAgentCards(), directives);
  const failures =
    raw.failures && raw.failures.length > 0 ? raw.failures : seedFailureCards();

  return {
    intakes: raw.intakes ?? [],
    graderResults: raw.graderResults ?? [],
    prompts: raw.prompts ?? [],
    artifactPlans: raw.artifactPlans ?? [],
    mergePlans: raw.mergePlans ?? [],
    directives,
    agents,
    failures,
    activeSurface: raw.activeSurface ?? "deck",
    presentationMode: raw.presentationMode ?? false,
    sovereign: raw.sovereign ?? buildSovereignStatus(),
  };
}

export function loadState(): BobState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return migrateState(JSON.parse(raw) as Partial<BobState>);
  } catch {
    return null;
  }
}

export function saveState(state: BobState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function downloadText(filename: string, content: string, mime = "text/plain"): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function uid(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export { migrateState };
